import React, { useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Upload, FileSpreadsheet, Check, X, Download, Trash2, Eye, 
  Merge, ArrowRight, FileUp, AlertCircle, RefreshCw, Plus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

export default function ExcelMergePage() {
  const [sessionId, setSessionId] = useState(null);
  const [destination, setDestination] = useState(null);
  const [destColumns, setDestColumns] = useState([]);
  const [sources, setSources] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [mergedFile, setMergedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [tempMappings, setTempMappings] = useState({});

  // Create new session
  const createSession = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/excel-merge/create-session`, {}, { headers: getAuthHeader() });
      setSessionId(res.data.session_id);
      setDestination(null);
      setDestColumns([]);
      setSources([]);
      setMergedFile(null);
      setPreviewData(null);
      toast.success('New merge session created');
    } catch (error) {
      toast.error('Failed to create session');
    }
  };

  // Upload destination template
  const handleDestinationUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!sessionId) {
      await createSession();
    }

    const currentSessionId = sessionId || (await axios.post(`${API_URL}/api/excel-merge/create-session`, {}, { headers: getAuthHeader() })).data.session_id;
    if (!sessionId) setSessionId(currentSessionId);

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(
        `${API_URL}/api/excel-merge/upload-destination/${currentSessionId}`,
        formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      setDestination({ filename: res.data.filename, columnCount: res.data.column_count });
      setDestColumns(res.data.columns);
      toast.success(`Destination template loaded: ${res.data.column_count} columns`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload destination file');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  // Upload source file
  const handleSourceUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (!sessionId || !destination) {
      toast.error('Please upload destination template first');
      return;
    }

    setIsUploading(true);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await axios.post(
          `${API_URL}/api/excel-merge/upload-source/${sessionId}`,
          formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
        );
        
        setSources(prev => [...prev, {
          id: res.data.source_id,
          filename: res.data.filename,
          rowCount: res.data.row_count,
          matchedColumns: res.data.matched_columns,
          matched: res.data.matched,
          unmatchedSource: res.data.unmatched_source,
          unmatchedDest: res.data.unmatched_dest
        }]);
        
        toast.success(`${res.data.filename}: ${res.data.matched_columns}/${res.data.total_columns} columns matched`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || 'Unknown error'}`);
      }
    }
    
    setIsUploading(false);
    setMergedFile(null);
    e.target.value = '';
  };

  // Remove source
  const removeSource = async (sourceId) => {
    try {
      await axios.delete(`${API_URL}/api/excel-merge/remove-source/${sessionId}/${sourceId}`, { headers: getAuthHeader() });
      setSources(prev => prev.filter(s => s.id !== sourceId));
      setMergedFile(null);
      toast.success('Source file removed');
    } catch (error) {
      toast.error('Failed to remove source');
    }
  };

  // Edit mappings
  const openMappingEditor = (source) => {
    setEditingSource(source);
    // Initialize temp mappings from current matched columns
    const mappings = {};
    source.matched.forEach(col => {
      mappings[col] = col;
    });
    setTempMappings(mappings);
    setShowMappingDialog(true);
  };

  const saveMappings = async () => {
    if (!editingSource) return;
    
    try {
      await axios.put(
        `${API_URL}/api/excel-merge/update-mapping/${sessionId}/${editingSource.id}`,
        tempMappings,
        { headers: getAuthHeader() }
      );
      
      // Update local state
      setSources(prev => prev.map(s => {
        if (s.id === editingSource.id) {
          return {
            ...s,
            matched: Object.keys(tempMappings).filter(k => tempMappings[k]),
            matchedColumns: Object.keys(tempMappings).filter(k => tempMappings[k]).length
          };
        }
        return s;
      }));
      
      setShowMappingDialog(false);
      setEditingSource(null);
      setMergedFile(null);
      toast.success('Mappings updated');
    } catch (error) {
      toast.error('Failed to update mappings');
    }
  };

  // Preview merge
  const previewMerge = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/excel-merge/preview/${sessionId}?limit=20`,
        {},
        { headers: getAuthHeader() }
      );
      setPreviewData(res.data);
      setShowPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    }
  };

  // Execute merge
  const executeMerge = async () => {
    setIsMerging(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/excel-merge/merge/${sessionId}`,
        {},
        { headers: getAuthHeader() }
      );
      setMergedFile({
        filename: res.data.filename,
        rowCount: res.data.row_count,
        columnCount: res.data.column_count,
        downloadUrl: res.data.download_url
      });
      toast.success(`Merge complete! ${res.data.row_count} rows merged`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Merge failed');
    } finally {
      setIsMerging(false);
    }
  };

  // Download merged file
  const downloadMerged = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/excel-merge/download/${sessionId}`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', mergedFile.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  // Reset session
  const resetSession = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API_URL}/api/excel-merge/session/${sessionId}`, { headers: getAuthHeader() });
      } catch (e) {
        // Ignore cleanup errors
      }
    }
    setSessionId(null);
    setDestination(null);
    setDestColumns([]);
    setSources([]);
    setMergedFile(null);
    setPreviewData(null);
  };

  const totalSourceRows = sources.reduce((sum, s) => sum + s.rowCount, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Merge className="w-6 h-6" />
              Excel Data Merge Tool
            </h1>
            <p className="text-muted-foreground mt-1">
              Import and merge data from multiple Excel files into a single destination template
            </p>
          </div>
          {sessionId && (
            <Button variant="outline" onClick={resetSession}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
          )}
        </div>

        {/* Step 1: Destination Template */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${destination ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>
                {destination ? <Check className="w-4 h-4" /> : '1'}
              </div>
              <div>
                <CardTitle className="text-lg">Upload Destination Template</CardTitle>
                <CardDescription>Excel file with target column headers (can be empty)</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!destination ? (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <FileUp className="w-8 h-8 mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Click to upload</span> destination template (.xlsx)
                  </p>
                </div>
                <input type="file" className="hidden" accept=".xlsx,.xls" onChange={handleDestinationUpload} disabled={isUploading} />
              </label>
            ) : (
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-medium">{destination.filename}</p>
                    <p className="text-sm text-muted-foreground">{destination.columnCount} columns defined</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setDestination(null); setDestColumns([]); setSources([]); setMergedFile(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Source Files */}
        <Card className={!destination ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${sources.length > 0 ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>
                {sources.length > 0 ? <Check className="w-4 h-4" /> : '2'}
              </div>
              <div>
                <CardTitle className="text-lg">Upload Source Files</CardTitle>
                <CardDescription>Excel files containing data to merge (supports multiple files)</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Upload area */}
            <label className="flex items-center justify-center w-full h-20 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold">Add source files</span> (.xlsx) - Multiple files supported
                </p>
              </div>
              <input type="file" className="hidden" accept=".xlsx,.xls" multiple onChange={handleSourceUpload} disabled={isUploading || !destination} />
            </label>

            {/* Source files list */}
            {sources.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                  <span>{sources.length} source file(s) uploaded</span>
                  <span>{totalSourceRows.toLocaleString()} total rows</span>
                </div>
                {sources.map(source => (
                  <div key={source.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="font-medium text-sm">{source.filename}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{source.rowCount.toLocaleString()} rows</span>
                          <span>•</span>
                          <Badge variant={source.matchedColumns > 0 ? 'default' : 'destructive'} className="text-xs">
                            {source.matchedColumns} columns matched
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openMappingEditor(source)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => removeSource(source.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 3: Preview & Merge */}
        <Card className={sources.length === 0 ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${mergedFile ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>
                {mergedFile ? <Check className="w-4 h-4" /> : '3'}
              </div>
              <div>
                <CardTitle className="text-lg">Preview & Merge</CardTitle>
                <CardDescription>Review the data and generate merged file</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={previewMerge} disabled={sources.length === 0}>
                <Eye className="w-4 h-4 mr-2" />
                Preview Data
              </Button>
              <Button onClick={executeMerge} disabled={sources.length === 0 || isMerging}>
                {isMerging ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Merge className="w-4 h-4 mr-2" />
                )}
                {isMerging ? 'Merging...' : 'Merge All Files'}
              </Button>
              
              {mergedFile && (
                <div className="flex items-center gap-3 ml-4 pl-4 border-l">
                  <div className="text-sm">
                    <span className="text-green-600 font-medium">✓ Merged:</span>
                    <span className="ml-2">{mergedFile.rowCount.toLocaleString()} rows</span>
                  </div>
                  <Button onClick={downloadMerged} className="bg-green-600 hover:bg-green-700">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Destination Columns Reference */}
        {destColumns.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Destination Columns ({destColumns.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-1">
                {destColumns.map((col, i) => (
                  <Badge key={i} variant="outline" className="text-xs font-mono">
                    {col}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Data Preview</DialogTitle>
            <DialogDescription>
              Showing first {previewData?.preview_rows?.length || 0} of {previewData?.total_rows?.toLocaleString() || 0} total rows
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {previewData && (
              <Table>
                <TableHeader>
                  <TableRow>
                    {previewData.columns?.slice(0, 15).map((col, i) => (
                      <TableHead key={i} className="text-xs whitespace-nowrap">{col}</TableHead>
                    ))}
                    {previewData.columns?.length > 15 && (
                      <TableHead className="text-xs">+{previewData.columns.length - 15} more</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.preview_rows?.map((row, i) => (
                    <TableRow key={i}>
                      {previewData.columns?.slice(0, 15).map((col, j) => (
                        <TableCell key={j} className="text-xs max-w-[150px] truncate">
                          {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                        </TableCell>
                      ))}
                      {previewData.columns?.length > 15 && (
                        <TableCell className="text-xs text-muted-foreground">...</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mapping Editor Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Column Mappings: {editingSource?.filename}</DialogTitle>
            <DialogDescription>
              Map source columns to destination columns. Only mapped columns will be copied.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {editingSource && (
              <div className="space-y-2">
                <div className="text-sm font-medium mb-2">Matched Columns</div>
                {editingSource.matched?.map(col => (
                  <div key={col} className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="font-mono">{col}</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <span className="font-mono text-green-700">{col}</span>
                  </div>
                ))}
                
                {editingSource.unmatchedSource?.length > 0 && (
                  <>
                    <div className="text-sm font-medium mt-4 mb-2">Unmatched Source Columns (ignored)</div>
                    <div className="flex flex-wrap gap-1">
                      {editingSource.unmatchedSource.map((col, i) => (
                        <Badge key={i} variant="secondary" className="text-xs font-mono">
                          {col}
                        </Badge>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMappingDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
